import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FiClock, FiUsers, FiStar, FiArrowRight } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useSettings } from '../contexts/SettingsContext';
import Button from './Button';

const Card = styled.div`
  background: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: ${props => props.theme.shadows.md};
  border: 1px solid ${props => props.theme.colors.gray[200]};
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: 100%;
  display: flex;
  flex-direction: ${props => props.$list ? 'row' : 'column'};
  position: relative;

  ${props => props.$list && `
    min-height: 220px;
    align-items: stretch;
  `}
  
    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      /* On smaller screens, stack vertically for readability */
      flex-direction: column;
      min-height: initial;
    }

  @media (hover: hover) and (pointer: fine) {
    /* Only apply hover effects on devices with precise pointers (desktop) */
    &:hover {
      transform: translateY(-8px) scale(1.02);
      box-shadow: ${props => props.theme.shadows['2xl']};
      border-color: ${props => props.theme.colors.primary}44;
    }
  }
`;

const ImageContainer = styled.div`
  position: relative;
  height: ${props => props.$list ? 'auto' : '200px'};
  min-height: ${props => props.$list ? '220px' : '200px'};
  width: ${props => props.$list ? '50%' : '100%'};
  flex: ${props => props.$list ? '1 0 50%' : 'none'};
  background: ${props => props.hasImage ? '#f0f0f0' : props.theme.colors.gradient.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.light};
  font-size: ${props => props.theme.fontSizes['4xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  text-align: center;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.hasImage ? 'rgba(0, 0, 0, 0.2)' : 'rgba(0, 0, 0, 0.1)'};
    z-index: 1;
  }
  
    @media (max-width: ${props => props.theme.breakpoints.tablet}) {
      width: 100%;
      height: 180px;
      min-height: 180px;
      flex: none;
    }

    @media (max-width: ${props => props.theme.breakpoints.mobile}) {
      height: 150px;
      min-height: 150px;
    }
`;

const CourseImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);

  ${Card}:hover & {
    transform: scale(1.05);
  }
`;

const IconWrapper = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${props => props.theme.fontSizes['4xl']};
`;

const Badge = styled.div`
  position: absolute;
  top: ${props => props.theme.spacing.md};
  right: ${props => props.theme.spacing.md};
  background: ${props => props.theme.colors.secondary};
  color: ${props => props.theme.colors.light};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.semibold};
  z-index: 1;
`;

const Content = styled.div`
  padding: ${props => props.theme.spacing.xl};
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;

  ${props => props.$list && `
    width: 50%;
    flex: 1 0 50%;
    gap: ${props.theme.spacing.md};
    justify-content: space-between;
  `}

  @media (max-width: ${props => props.theme.breakpoints.tablet}) {
    width: 100%;
    flex: 1 0 auto;
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: ${props => props.theme.spacing.md};
  }
`;

const Title = styled.h3`
  font-size: ${props => props.theme.fontSizes.xl};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.dark};
  margin-bottom: ${props => props.theme.spacing.sm};

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: ${props => props.theme.fontSizes.base};
    margin-bottom: ${props => props.theme.spacing.xs};
  }
`;

const Description = styled.p`
  color: ${props => props.theme.colors.gray[600]};
  line-height: 1.6;
  margin-bottom: ${props => props.theme.spacing.lg};
  flex: 1;

  ${props => props.$list && `
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    line-clamp: 2;
    max-height: 3.2em; /* 2 lines x 1.6 line-height */
    margin-bottom: ${props.theme.spacing.md};
  `}

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-bottom: ${props => props.theme.spacing.sm};
    font-size: ${props => props.theme.fontSizes.xs};
    line-height: 1.4;
    display: none;
  }
`;

const MetaInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.lg};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.gray[600]};

  .meta-item {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing.xs};

    svg {
      color: ${props => props.theme.colors.primary};
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    gap: ${props => props.theme.spacing.sm};
    margin-bottom: ${props => props.theme.spacing.sm};
    font-size: 0.7rem;
  }
`;

const Price = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing.lg};

  .current-price {
    font-size: ${props => props.theme.fontSizes.xl};
    font-weight: ${props => props.theme.fontWeights.bold};
    color: ${props => props.theme.colors.primary};
  }

  .original-price {
    font-size: ${props => props.theme.fontSizes.base};
    color: ${props => props.theme.colors.gray[500]};
    text-decoration: line-through;
    margin-left: ${props => props.theme.spacing.sm};
  }

  .rating {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing.xs};
    color: ${props => props.theme.colors.gray[600]};
    font-size: ${props => props.theme.fontSizes.sm};

    svg {
      color: #fbbf24;
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    margin-bottom: ${props => props.theme.spacing.sm};

    .current-price {
      font-size: ${props => props.theme.fontSizes.base};
    }
    
    .rating {
      font-size: 0.75rem;
      
      svg {
        width: 14px;
        height: 14px;
      }
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  ${props => props.$list && `
    justify-content: flex-end;
  `}
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.xs};
    
    button, a {
      padding: 8px 12px !important;
      font-size: 0.8rem !important;
      min-height: auto !important;
    }
  }
`;

const CourseCard = ({ course, viewMode = 'grid' }) => {
  const {
    title,
    description,
    shortDescription,
    shortDesc,
    summary,
    subtitle,
    brief,
    tagline,
    duration,
    students,
    rating,
    currentPrice,
    originalPrice,
    newCourse,
    slug,
    icon,
    image,
    showPrice = true,
    showStudents = true,
    showDuration = true
  } = course;

  const { settings } = useSettings();

  const formatWhatsAppUrl = (raw, text) => {
    if (!raw) return '';
    const number = String(raw).replace(/[^\d]/g, '');
    if (!number) return '';
    const query = text ? `?text=${encodeURIComponent(text)}` : '';
    return `https://wa.me/${number}${query}`;
  };

  const whatsappUrl = formatWhatsAppUrl(settings?.social?.whatsapp, `Hi, I would like to know about ${title} course pricing`);

  const isList = viewMode === 'list';
  const listDescription = [shortDescription, shortDesc, summary, subtitle, brief, tagline, description].find(Boolean);

  return (
    <Card $list={isList} className={isList ? 'elevate-on-hover' : ''}>
      <ImageContainer hasImage={!!image} $list={isList}>
        {image ? (
          <CourseImage src={image} alt={title} onError={(e) => {
            // If image fails to load, hide it and show icon
            e.target.style.display = 'none';
          }} />
        ) : (
          <IconWrapper>
            {icon ? icon : title.split(' ').map(word => word[0]).join('')}
          </IconWrapper>
        )}
        {newCourse && <Badge>New</Badge>}
      </ImageContainer>
      
      <Content $list={isList}>
        <Title>{title}</Title>
        {isList ? (
          listDescription && <Description $list>{listDescription}</Description>
        ) : (
          description && <Description>{description}</Description>
        )}
        
        {(showDuration || showStudents) && (
          <MetaInfo>
            {showDuration && duration && (
              <div className="meta-item">
                <FiClock />
                <span>{duration}</span>
              </div>
            )}
            {showStudents && students && (
              <div className="meta-item">
                <FiUsers />
                <span>{students} students</span>
              </div>
            )}
          </MetaInfo>
        )}
        
        {(showPrice || rating) && (
          <Price>
            {showPrice && (
              <div>
                {currentPrice && parseFloat(currentPrice) > 0 ? (
                  <>
                    <span className="current-price">₹{currentPrice}</span>
                    {originalPrice && (
                      <span className="original-price">₹{originalPrice}</span>
                    )}
                  </>
                ) : (
                  whatsappUrl ? (
                    <a 
                      href={whatsappUrl}
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: '#25D366', 
                        fontWeight: 600,
                        fontSize: '1rem',
                        textDecoration: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <FaWhatsapp size={20} />
                      <span>Contact for Pricing</span>
                    </a>
                  ) : (
                    <span style={{ fontWeight: 600 }}>Contact for Pricing</span>
                  )
                )}
              </div>
            )}
            {rating && (
              <div className="rating">
                <FiStar />
                <span>{rating}</span>
              </div>
            )}
          </Price>
        )}
        
        <ButtonContainer $list={isList}>
          <Button
            as={Link}
            to={`/courses/${slug}`}
            variant="primary"
            size={isList ? 'small' : 'medium'}
            style={{ flex: isList ? '0 0 auto' : 1, minWidth: isList ? 160 : undefined }}
          >
            Learn More
            <FiArrowRight />
          </Button>
        </ButtonContainer>
      </Content>
    </Card>
  );
};

export default CourseCard;